// src/hooks/common/useTopicsDataSync.ts
import { useAppEvent } from '@/hooks/common/useAppEvent'
import { AppEvents } from '@/events/eventTypes'
import useAppStore from '@/store/useAppStore'
import { Ego, ITopicCacheSlice, Topic } from '@/types'
import { PrivacyType, TopicType } from '@/utils/constants'
import { TopicsCacheKey } from '@/utils/cacheKeys'

/** Maps a newly created topic's type to its Workspace cache bucket */

export const useTopicsDataSync = () => {
  const workspacePatternsCacheSlice = useAppStore(
    (state) => state[TopicsCacheKey.Workspace_Patterns] as ITopicCacheSlice,
  )
  const workspaceCollectionsCacheSlice = useAppStore(
    (state) => state[TopicsCacheKey.Workspace_Collections] as ITopicCacheSlice,
  )
  const workspacePinsCacheSlice = useAppStore(
    (state) => state[TopicsCacheKey.Workspace_Pins] as ITopicCacheSlice,
  )

  const discoverPatternsCacheSlice = useAppStore(
    (state) => state[TopicsCacheKey.Discover_Patterns] as ITopicCacheSlice,
  )
  const discoverCollectionsCacheSlice = useAppStore(
    (state) => state[TopicsCacheKey.Discover_Collections] as ITopicCacheSlice,
  )
  const discoverPinsCacheSlice = useAppStore(
    (state) => state[TopicsCacheKey.Discover_Pins] as ITopicCacheSlice,
  )

  const allCacheSlices = [
    workspacePatternsCacheSlice,
    workspaceCollectionsCacheSlice,
    workspacePinsCacheSlice,
    discoverPatternsCacheSlice,
    discoverCollectionsCacheSlice,
    discoverPinsCacheSlice,
  ]

  useAppEvent(AppEvents.TOPIC_ADDED, ({ topic }) => {
    if (topic.topicType == TopicType.PATTERN.toString()) {
      workspacePatternsCacheSlice.addTopicToCache(topic, 'start')
    } else if (topic.topicType == TopicType.ALBUM.toString()) {
      workspaceCollectionsCacheSlice.addTopicToCache(topic, 'start')
    } else if (topic.topicType == TopicType.PIN_COLLECTION.toString()) {
      workspacePinsCacheSlice.addTopicToCache(topic, 'start')
    }
  })

  useAppEvent(AppEvents.TOPIC_UPDATED, ({ topic }) => {
    allCacheSlices.forEach((slice) => {
      slice.updateTopicInCache(topic.topicID, (existing) => ({ ...existing, ...topic }))
    })
  })

  useAppEvent(AppEvents.TOPIC_DELETED, ({ topicID }) => {
    allCacheSlices.forEach((slice) => {
      slice.removeTopicFromCache(topicID)
    })
  })

  useAppEvent(AppEvents.TOPIC_PRIVACY_UPDATED, ({ topic }) => {
    if (topic.visibility == PrivacyType.PRIVATE.toString()) {
      // Remove from Discover, Add/update to Workspace ()

      if (topic.topicType == TopicType.PATTERN.toString()) {
        workspacePatternsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )
        discoverPatternsCacheSlice.removeTopicFromCache(topic.topicID)
      } else if (topic.topicType == TopicType.ALBUM.toString()) {
        workspaceCollectionsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )

        discoverCollectionsCacheSlice.removeTopicFromCache(topic.topicID)
      } else if (topic.topicType == TopicType.PIN_COLLECTION.toString()) {
        workspacePinsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )
        discoverPinsCacheSlice.removeTopicFromCache(topic.topicID)
      }
    } else if (topic.visibility == PrivacyType.PUBLIC.toString()) {
      // add or update to Discover and add/update to workspace

      if (topic.topicType == TopicType.PATTERN.toString()) {
        discoverPatternsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )

        workspacePatternsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )
      } else if (topic.topicType == TopicType.ALBUM.toString()) {
        discoverCollectionsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )

        workspaceCollectionsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )
      } else if (topic.topicType == TopicType.PIN_COLLECTION.toString()) {
        discoverPinsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )

        workspacePinsCacheSlice.addOrUpdateTopicInCache(
          topic.topicID,
          (existing) => (existing ? { ...existing, ...topic } : topic),
          'start',
        )
      }
    }
  })

  useAppEvent(AppEvents.TOPIC_NAME_UPDATED, ({ topicID, topicName }) => {
    allCacheSlices.forEach((slice) => {
      slice.updateTopicInCache(topicID, (topic) => ({
        ...topic,
        topicName,
      }))
    })
  })

  useAppEvent(AppEvents.TOPIC_NOTE_ADDED, ({ topicID, note }) => {
    allCacheSlices.forEach((slice) => {
      slice.updateTopicInCache(topicID, (topic) => ({
        ...topic,
        latestNote: note,
      }))
    })
  })

  useAppEvent(AppEvents.TOPIC_NOTE_DELETED, ({ topicID, noteID }) => {
    allCacheSlices.forEach((slice) => {
      slice.updateTopicInCache(topicID, (topic) => ({
        ...topic,
        latestEgo:
          topic.latestNote.noteID == noteID ? (undefined as unknown as Ego) : topic.latestEgo,
        latestNote:
          topic.latestNote.noteID == noteID
            ? { ...topic.firstNote, egoList: [] }
            : topic.latestNote,
        firstNote:
          topic.latestNote.noteID == noteID ? { ...topic.firstNote, egoList: [] } : topic.firstNote,
      }))
    })
  })

  useAppEvent(AppEvents.NOTE_LIKE_STATUS_UPDATED, ({ topicID, noteID, isLiked }) => {
    const delta = isLiked ? 1 : -1

    allCacheSlices.forEach((slice) => {
      slice.updateTopicInCache(topicID, (topic) => ({
        ...topic,
        firstNote:
          topic.firstNote.noteID === noteID
            ? {
                ...topic.firstNote,
                isThumbsUp: isLiked ? 1 : 0,
                thumbsUp: String(Number(topic.firstNote.thumbsUp || 0) + delta),
              }
            : topic.firstNote,
        totalLikes: String(Math.max(Number(topic.totalLikes || 0) + delta, 0)),
      }))
    })
  })

  // TODO: add comment directly to note details.
  useAppEvent(AppEvents.NOTE_COMMENT_ADDED, ({ topicID, noteID, comment }) => {
    allCacheSlices.forEach((slice) => {
      slice.updateTopicInCache(topicID, (topic) => ({
        ...topic,
        firstNote:
          topic.firstNote.noteID === noteID
            ? {
                ...topic.firstNote,
                comments: topic.firstNote.comments + 1,
              }
            : topic.firstNote,
        totalComments: String(Number(topic.totalComments || 0) + 1),
      }))
    })
  })

  useAppEvent(AppEvents.NOTE_COMMENT_DELETED, ({ topicID, noteID }) => {
    allCacheSlices.forEach((slice) => {
      slice.updateTopicInCache(topicID, (topic) => ({
        ...topic,
        firstNote:
          topic.firstNote.noteID === noteID
            ? {
                ...topic.firstNote,
                comments: Math.max(topic.firstNote.comments - 1, 0),
              }
            : topic.firstNote,
        totalComments: String(Math.max(Number(topic.totalComments || 0) - 1, 0)),
      }))
    })
  })

  useAppEvent(AppEvents.TOPIC_VIEWED, ({ topicID }) => {
    allCacheSlices.forEach((slice) => {
      slice.updateTopicInCache(topicID, (topic) => ({
        ...topic,
        visitorVisits: String(Number(topic.visitorVisits || 0) + 1),
      }))
    })
  })
}
